// features/settings/InviteLinkCard.tsx — Settings → Account "Invite Friends" (1.24.00 B2;
// dashboard card since 1.27.70).
//
// The member's one active personal link, built by buildJoinUrl(code) from the
// live origin (never the server's `url` field, so a dev build never hands out
// a production link). Three real controls:
//   Copy        clipboard + toast (guards a missing navigator.clipboard)
//   Share via…  ONLY when `typeof navigator.share === 'function'` — a tile that
//               opens nothing is the inert-control anti-pattern this repo bans
//   Regenerate  behind ConfirmDialog (it retires the old link for good) →
//               POST /users/me/invite/regenerate/
// Anchored as #invite so the account-popover "Invite friends" item can land here.

import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Button, useToast } from '@/ui';
import { Icon } from '@/icons';
import { useInviteLink, useSession } from '@/hooks';
import { buildJoinUrl } from '@/core/lib/postUrl';
import { ConfirmDialog } from '@/components/ConfirmDialog/ConfirmDialog';
import { CardHead, DashCard, Pill } from './dashboard/DashCard';

const Lead = styled.p`
  margin: 16px 0 4px;
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: 600;
  color: ${({ theme }) => theme.color.textPrimary};
`;

const Hint = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSize.xs};
  line-height: ${({ theme }) => theme.lineHeight.xs};
  color: ${({ theme }) => theme.color.textMuted};
  max-width: 60ch;
`;

const UrlDisplay = styled.code`
  display: block;
  margin-top: 14px;
  padding: 9px 12px;
  background: ${({ theme }) => theme.color.glassBg};
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  border-radius: ${({ theme }) => theme.radius.md};
  font-family: ${({ theme }) => theme.font.mono};
  font-variant-numeric: tabular-nums;
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textPrimary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  user-select: all;
`;

const Meta = styled.span`
  display: block;
  margin-top: 6px;
  font-size: ${({ theme }) => theme.fontSize.xs};
  font-variant-numeric: tabular-nums;
  color: ${({ theme }) => theme.color.textMuted};
`;

const Actions = styled.div`
  margin-top: auto;
  padding-top: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 14px;
`;

const StateText = styled.p`
  margin: 14px 0 0;
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;

export const INVITE_ANCHOR_ID = 'invite';

export function InviteLinkCard() {
  const { invite, loading, error, refresh, regenerate } = useInviteLink();
  const { currentUser } = useSession();
  const toast = useToast();
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const cardRef = useRef<HTMLElement>(null);
  const canShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';

  // Land here from the account popover's "Invite friends" (/settings/account#invite).
  useEffect(() => {
    if (window.location.hash === `#${INVITE_ANCHOR_ID}`) {
      cardRef.current?.scrollIntoView?.({ block: 'start' });
    }
  }, []);

  const url = invite ? buildJoinUrl(invite.code) : '';

  async function handleCopy() {
    if (!url) return;
    if (!navigator.clipboard) {
      toast('Could not copy link');
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
      toast('Invite link copied');
    } catch {
      toast('Could not copy link');
    }
  }

  async function handleShare() {
    if (!url || !canShare) return;
    try {
      await navigator.share({ title: 'Join me on TruXSocial', url });
    } catch (e) {
      // AbortError = the member closed the sheet; anything else is worth a word.
      if (!(e instanceof Error && e.name === 'AbortError')) toast('Could not open the share sheet');
    }
  }

  async function handleRegenerate() {
    setConfirming(false);
    setBusy(true);
    const ok = await regenerate();
    setBusy(false);
    toast(ok ? 'New invite link ready. The old one no longer works' : 'Could not regenerate the link');
  }

  const invitedTotal = currentUser?.invited ?? invite?.usesCount ?? 0;

  return (
    <DashCard ref={cardRef} id={INVITE_ANCHOR_ID} labelledBy="invite-link-title" testId="invite-card">
      <CardHead
        icon="user-plus"
        title="Invite Friends"
        titleId="invite-link-title"
        aside={<Pill tone="accent">{invitedTotal} invited</Pill>}
      />
      <Lead>Share TruXSocial with your friends</Lead>
      <Hint>
        Anyone who joins through your link counts toward your Invited number. Making a new link
        retires this one. People who already joined stay counted.
      </Hint>

      {loading && <StateText role="status">Loading your link…</StateText>}
      {!loading && error && (
        <Row role="status">
          <StateText as="span">{error}</StateText>
          <Button variant="secondary" size="sm" onClick={refresh}>Try again</Button>
        </Row>
      )}
      {!loading && invite && (
        <>
          <UrlDisplay title={url} data-testid="invite-url">{url}</UrlDisplay>
          <Meta>
            {invite.usesCount === 1 ? '1 person has joined' : `${invite.usesCount} people have joined`} through this link
          </Meta>
          <Actions>
            <Button variant="primary" size="sm" onClick={() => void handleCopy()}>
              <Icon name="copy" size={14} /> Copy link
            </Button>
            {canShare && (
              <Button variant="secondary" size="sm" onClick={() => void handleShare()}>
                <Icon name="share" size={14} /> Share via…
              </Button>
            )}
            <Button variant="secondary" size="sm" onClick={() => setConfirming(true)} disabled={busy}>
              <Icon name="refresh" size={14} /> {busy ? 'Regenerating…' : 'Regenerate'}
            </Button>
          </Actions>
        </>
      )}

      {confirming && (
        <ConfirmDialog
          title="Make a new invite link?"
          message="Your current link stops working immediately. Anyone who already joined through it stays counted."
          confirmLabel="Regenerate"
          onConfirm={() => void handleRegenerate()}
          onClose={() => setConfirming(false)}
        />
      )}
    </DashCard>
  );
}
